/**
 * VarianceTrendChart — Per-cashier Over/Short variance chart.
 *
 * Over and Short are tracked SEPARATELY — they never cancel each other.
 * Over = unrecorded sales or customer overpayment.
 * Short = potential theft or change errors.
 *
 * Uses Recharts BarChart with stacked bars per period bucket.
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '../../contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { Button } from '../ui/button';
import { TrendingUp, TrendingDown, Minus, BarChart3 } from 'lucide-react';

const COLORS = [
  '#1A4D2E', '#2563eb', '#d97706', '#dc2626', '#7c3aed',
  '#0891b2', '#be185d', '#65a30d', '#ea580c', '#6366f1',
];

function fmtPHP(n) {
  return parseFloat(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function VarianceTrendChart({ branchId }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('weekly');
  const [loading, setLoading] = useState(false);

  const fetchTrend = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period, days: 90 };
      if (branchId) params.branch_id = branchId;
      const res = await api.get('/shifts/variance-trend', { params });
      setData(res.data);
    } catch {
      setData(null);
    }
    setLoading(false);
  }, [period, branchId]);

  useEffect(() => { fetchTrend(); }, [fetchTrend]);

  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4 text-center text-sm text-slate-400">
        Loading variance trend...
      </div>
    );
  }

  if (!data || !data.buckets?.length) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4 text-center text-sm text-slate-400">
        <BarChart3 size={20} className="mx-auto mb-1 text-slate-300" />
        No closed shift data for the last 90 days.
      </div>
    );
  }

  const cashierNames = Object.keys(data.cashier_summary || {});

  // Build chart data — each bucket becomes a row with per-cashier over/short
  const chartData = data.buckets.map(b => {
    const row = { label: b.label };
    // Aggregate over and short totals for the chart
    row.over = b.over_total;
    row.short = b.short_total;
    return row;
  });

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const bucket = data.buckets.find(b => b.label === label);
    if (!bucket) return null;
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs max-w-[260px]">
        <p className="font-semibold text-slate-800 mb-2">{label}</p>
        {Object.entries(bucket.cashiers || {}).map(([name, c]) => (
          <div key={name} className="flex justify-between gap-4 py-0.5">
            <span className="text-slate-600 truncate">{name}</span>
            <span className="shrink-0">
              {c.over_total > 0 && <span className="text-blue-600 font-medium">+P{fmtPHP(c.over_total)}</span>}
              {c.over_total > 0 && c.short_total > 0 && ' / '}
              {c.short_total > 0 && <span className="text-red-600 font-medium">-P{fmtPHP(c.short_total)}</span>}
              {c.over_total === 0 && c.short_total === 0 && <span className="text-emerald-600">Balanced</span>}
            </span>
          </div>
        ))}
        <div className="border-t border-slate-100 mt-1.5 pt-1.5 flex justify-between font-semibold">
          <span className="text-blue-600">Over: +P{fmtPHP(bucket.over_total)}</span>
          <span className="text-red-600">Short: -P{fmtPHP(bucket.short_total)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="variance-trend-chart">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <BarChart3 size={15} className="text-slate-600" />
            Cashier Variance Trend
          </h3>
          <p className="text-[11px] text-slate-400 mt-0.5">Over and Short tracked separately — last 90 days</p>
        </div>
        <div className="flex gap-1">
          <Button
            size="sm" variant={period === 'weekly' ? 'default' : 'outline'}
            className={`h-7 text-[11px] px-2.5 ${period === 'weekly' ? 'bg-[#1A4D2E] text-white' : ''}`}
            onClick={() => setPeriod('weekly')}
            data-testid="trend-weekly"
          >Weekly</Button>
          <Button
            size="sm" variant={period === 'monthly' ? 'default' : 'outline'}
            className={`h-7 text-[11px] px-2.5 ${period === 'monthly' ? 'bg-[#1A4D2E] text-white' : ''}`}
            onClick={() => setPeriod('monthly')}
            data-testid="trend-monthly"
          >Monthly</Button>
        </div>
      </div>

      {/* Chart */}
      <div className="px-3 pt-3 pb-1" style={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis
              dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }}
              tickFormatter={v => period === 'weekly' ? v.replace(/^\d{4}-/, '') : v.slice(5)}
            />
            <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => `P${v}`} width={55} />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: 11 }}
              formatter={(value) => value === 'over' ? 'Over (excess cash)' : 'Short (missing cash)'}
            />
            <Bar dataKey="over" name="over" fill="#2563eb" radius={[3, 3, 0, 0]} maxBarSize={40} />
            <Bar dataKey="short" name="short" fill="#dc2626" radius={[3, 3, 0, 0]} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Per-Cashier Summary Cards */}
      <div className="px-4 pb-4 pt-2">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Per-Cashier Accountability (90 days)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {cashierNames.map((name, i) => {
            const c = data.cashier_summary[name];
            const balancedPct = c.shift_count > 0 ? Math.round(c.balanced_count / c.shift_count * 100) : 0;
            return (
              <div key={name} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-100 bg-slate-50/50" data-testid={`cashier-card-${name.replace(/\s+/g, '-')}`}>
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0"
                  style={{ backgroundColor: COLORS[i % COLORS.length] }}
                >{name[0]}</div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-800 truncate">{name}</p>
                  <div className="flex gap-3 mt-0.5 text-[10px]">
                    <span className="text-blue-600 font-medium flex items-center gap-0.5">
                      <TrendingUp size={9} /> +P{fmtPHP(c.over_total)}
                    </span>
                    <span className="text-red-600 font-medium flex items-center gap-0.5">
                      <TrendingDown size={9} /> -P{fmtPHP(c.short_total)}
                    </span>
                    <span className="text-emerald-600 flex items-center gap-0.5">
                      <Minus size={9} /> {balancedPct}%
                    </span>
                  </div>
                  <p className="text-[9px] text-slate-400 mt-0.5">{c.shift_count} shifts · {c.balanced_count} balanced</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
